import { useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../../api/client";
import { PageHeader } from "../../../components/ComingSoon";
import { Button, Field, TextInput } from "../../../components/ui";
import { Passkeys, TrustedDevices } from "./SignInMethods";
import { CrewAlertsCard } from "./CrewAlertsCard";

interface Enroll { secret: string; otpauth_uri: string; qr_svg_data_uri: string; expires_minutes: number }

function Codes({ codes, testId }: { codes: string[]; testId: string }) {
  return (
    <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4" data-testid={testId}>
      <p className="text-sm text-amber-200">Shown once. Save them somewhere safe; each code works one time.</p>
      <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm text-white sm:grid-cols-5">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
    </div>
  );
}

function ReplaceAuthenticator() {
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [enroll, setEnroll] = useState<Enroll | null>(null);
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); } };
  const start = () => run(async () => { setEnroll(await api.post<Enroll>("/admin/me/security/authenticator/start", { password: pw, code })); setPw(""); setCode(""); });
  const confirm = () => run(async () => {
    const r = await api.post<{ message: string }>("/admin/me/security/authenticator/confirm", { code: newCode });
    toast.success(r.message); setEnroll(null); setNewCode("");
  });
  return (
    <section className="glass rounded-[20px] p-6" data-testid="security-authenticator-card">
      <h2 className="flex items-center gap-2 text-lg font-medium text-white"><ShieldCheck className="h-5 w-5 text-signal" />Set up a new authenticator</h2>
      <p className="mt-1 text-sm text-zinc-400">Use this if you got a new phone or deleted your authenticator entry. Your current one keeps working until the new one is verified.</p>
      {!enroll ? (
        <div className="mt-5 grid max-w-xl gap-4">
          <Field label="Current password" htmlFor="security-current-password"><TextInput id="security-current-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="security-current-password" /></Field>
          <Field label="Current 6-digit code or a backup code" htmlFor="security-current-code"><TextInput id="security-current-code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="security-current-code" /></Field>
          <div><Button onClick={start} loading={busy} disabled={!pw || code.length < 6} data-testid="security-start-replace">Continue</Button></div>
        </div>
      ) : (
        <div className="mt-5 grid gap-6 md:grid-cols-[220px_1fr]" data-testid="security-new-qr-step">
          <img src={enroll.qr_svg_data_uri} alt="QR code for Gameday Time authenticator" className="h-[220px] w-[220px] rounded-xl bg-snow p-3" data-testid="security-new-qr" />
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">Scan with your authenticator app. It will show <strong className="text-white">Gameday Time</strong>. Or enter this key manually (time-based):</p>
            <p className="break-all font-mono text-sm text-white" data-testid="security-new-secret">{enroll.secret}</p>
            <Field label="6-digit code from the NEW entry" htmlFor="security-new-code"><TextInput id="security-new-code" value={newCode} onChange={(e) => setNewCode(e.target.value)} data-testid="security-new-code" /></Field>
            <div className="flex gap-3">
              <Button onClick={confirm} loading={busy} disabled={newCode.length !== 6} data-testid="security-confirm-replace">Verify and switch</Button>
              <Button variant="ghost" onClick={() => setEnroll(null)} data-testid="security-cancel-replace">Cancel</Button>
            </div>
            <p className="text-xs text-zinc-500">This setup expires in {enroll.expires_minutes} minutes.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function BackupCodes() {
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { setCodes((await api.post<{ backup_codes: string[] }>("/admin/me/security/backup-codes", { code })).backup_codes); setCode(""); toast.success("New backup codes created"); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <section className="glass rounded-[20px] p-6" data-testid="security-backup-card">
      <h2 className="flex items-center gap-2 text-lg font-medium text-white"><KeyRound className="h-5 w-5 text-signal" />Generate new backup codes</h2>
      <p className="mt-1 text-sm text-zinc-400">Creates 10 new one-time codes. All old backup codes stop working.</p>
      <div className="mt-5 flex max-w-xl items-end gap-3">
        <div className="flex-1"><Field label="Current 6-digit code or a backup code" htmlFor="security-backup-code-input"><TextInput id="security-backup-code-input" value={code} onChange={(e) => setCode(e.target.value)} data-testid="security-backup-code-input" /></Field></div>
        <Button onClick={go} loading={busy} disabled={code.length < 6} data-testid="security-generate-backup">Generate</Button>
      </div>
      {codes && <Codes codes={codes} testId="security-backup-codes" />}
    </section>
  );
}

export default function MySecurity() {
  return (
    <div data-testid="admin-my-security-page" className="animate-rise space-y-6">
      <PageHeader title="My security" />
      <Passkeys />
      <TrustedDevices />
      <ReplaceAuthenticator />
      <BackupCodes />
      <CrewAlertsCard />
    </div>
  );
}
